// ========================================
// STATS & SKILLS COMPONENT - WEDDING EVENT
// ========================================

import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import '../../../assets/css/customer/about/StatsSkills.css';

const StatsSkills = () => {
  const [counters, setCounters] = useState({
    couples: 0,
    weddings: 0,
    decoration: 0,
    locations: 0
  });

  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);

  // Stats data
  const stats = [
    { key: 'couples', end: 156, label: 'Happy Couples', suffix: '+' },
    { key: 'weddings', end: 1680, label: 'Weddings', suffix: '+' },
    { key: 'decoration', end: 845, label: 'Decoration', suffix: '+' },
    { key: 'locations', end: 175, label: 'Locations', suffix: '+' }
  ];

  // Skills data
  const skills = [
    { name: 'Professionality', percentage: 92 },
    { name: 'Food Ordering', percentage: 85 },
    { name: 'Experience And Quality', percentage: 95 },
    { name: 'Guarantee', percentage: 88 }
  ];

  // Animate counter function
  const animateCounter = (key, end, duration = 2000) => {
    const startTime = Date.now();
    
    const animate = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      const current = Math.floor(progress * end);
      
      setCounters(prev => ({ ...prev, [key]: current }));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    animate();
  };

  // Intersection Observer for scroll animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            
            // Start all counters animation
            stats.forEach(stat => {
              animateCounter(stat.key, stat.end);
            });
          }
        });
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [hasAnimated]);

  return (
    <section className="stats-skills-section" ref={sectionRef}>
      <Container>
        {/* Stats Box - Top Section */}
        <div className="stats-box">
          <Row>
            {stats.map((stat, index) => (
              <Col 
                key={stat.key} 
                lg={3} 
                md={6} 
                sm={6} 
                xs={6} 
                className="stat-col"
              >
                <div className="stat-item">
                  <h3 className="stat-number">
                    {counters[stat.key]}{stat.suffix}
                  </h3>
                  <p className="stat-label">{stat.label}</p>
                </div>
                {index < stats.length - 1 && (
                  <div className="stat-divider"></div>
                )}
              </Col>
            ))}
          </Row>
        </div>

        {/* Skills Section - Bottom Section */}
        <Row className="skills-row">
          {/* Left Side - Content */}
          <Col lg={6} md={12} className="skills-content-col">
            <div className="skills-content">
              {/* Icon with Fun Facts Label */}
              <div className="fun-facts-label">
                <svg width="50" height="50" viewBox="0 0 50 50" fill="none">
                  <path 
                    d="M25 10 L28 18 L36 18 L30 23 L32 31 L25 26 L18 31 L20 23 L14 18 L22 18 Z" 
                    fill="#ffb8b8" 
                    opacity="0.6"
                  />
                  <circle cx="15" cy="15" r="2" fill="#e8b4b8" opacity="0.5"/>
                  <circle cx="35" cy="15" r="2" fill="#e8b4b8" opacity="0.5"/>
                  <circle cx="40" cy="30" r="1.5" fill="#ffb8b8" opacity="0.4"/>
                </svg>
                <span className="fun-facts-text">Bridely Fun Facts</span>
              </div>

              {/* Main Heading */}
              <h2 className="skills-heading">
                Explore our<br />
                wedding Services
              </h2>

              {/* Description */}
              <p className="skills-description">
                Experience elegant wedding planning with smart tools, seamless booking, and personalized recommendations
              </p>
            </div>
          </Col>

          {/* Right Side - Progress Bars */}
          <Col lg={6} md={12} className="skills-bars-col">
            <div className="skills-bars">
              {skills.map((skill, index) => (
                <div key={index} className="skill-item">
                  <div className="skill-header">
                    <span className="skill-name">{skill.name}</span>
                    <span className="skill-percentage">{skill.percentage}%</span>
                  </div>
                  <div className="progress-bar-container">
                    <div 
                      className="progress-bar-fill"
                      style={{ 
                        width: hasAnimated ? `${skill.percentage}%` : '0%',
                        transitionDelay: `${index * 0.2}s`
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default StatsSkills;